import {
  AlertTriangle,
  CheckCircle2,
  HelpCircle,
  Info,
  XCircle,
  type LucideIcon,
} from 'lucide-react';
import { Component, useCallback, useState, type ReactNode } from 'react';
import { ConfirmDialog } from '../../../components/ui';
import { useErrorText, useT } from '../../../i18n';
import { cn } from '../../../lib/cn';
import { AutomationError } from '../client';
import type { ReadinessStatus } from '../testrun-types';

/** Pieces the accounts pages share: notices, readiness icons, confirmations, tabs. */

/* ─── errors ─── */

/** TestRun's own messages are for people and are shown as they are; unreachable has its own words. */
export const useTestRunErrorText = () => {
  const { t } = useT();
  const { errorText } = useErrorText();
  return useCallback(
    (error: unknown): string =>
      error instanceof AutomationError && error.status === 0
        ? t('automation.unreachable')
        : errorText(error),
    [t, errorText],
  );
};

/** For ErrorState, which reads the message of a plain Error. */
export const useReadableError = () => {
  const text = useTestRunErrorText();
  return (error: unknown): Error => new Error(text(error));
};

/* ─── notices ─── */

export type NoticeTone = 'fail' | 'pass' | 'info' | 'warn';

const TONES: Record<NoticeTone, { box: string; icon: string; Icon: LucideIcon }> = {
  fail: { box: 'border-fail/40 bg-fail-soft', icon: 'text-fail', Icon: XCircle },
  pass: { box: 'border-pass/40 bg-pass-soft', icon: 'text-pass', Icon: CheckCircle2 },
  info: { box: 'border-border bg-sunken', icon: 'text-ink-muted', Icon: Info },
  warn: { box: 'border-block/40 bg-block-soft', icon: 'text-block', Icon: AlertTriangle },
};

export const Notice = ({
  tone,
  title,
  children,
  className,
}: {
  tone: NoticeTone;
  title?: ReactNode;
  children?: ReactNode;
  className?: string;
}) => {
  const { box, icon, Icon } = TONES[tone];
  return (
    <div
      role={tone === 'fail' ? 'alert' : 'status'}
      className={cn(
        'flex items-start gap-2 rounded-lg border px-3 py-2 text-[13px]',
        box,
        className,
      )}
    >
      <Icon className={cn('mt-0.5 size-4 shrink-0', icon)} aria-hidden="true" />
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        {title ? <p className="font-semibold text-ink">{title}</p> : null}
        {children ? (
          <div className="break-words text-ink-muted [overflow-wrap:anywhere]">{children}</div>
        ) : null}
      </div>
    </div>
  );
};

/** A failed action: what failed, and TestRun's reason. */
export const Fail = ({ title, error }: { title: string; error: unknown }) => {
  const text = useTestRunErrorText();
  return (
    <Notice tone="fail" title={title}>
      {text(error)}
    </Notice>
  );
};

/* ─── readiness ─── */

const READINESS: Record<ReadinessStatus, { Icon: LucideIcon; className: string }> = {
  ok: { Icon: CheckCircle2, className: 'text-pass' },
  warn: { Icon: AlertTriangle, className: 'text-block' },
  fail: { Icon: XCircle, className: 'text-fail' },
  unknown: { Icon: HelpCircle, className: 'text-ink-subtle' },
};

/** A readiness status: an icon of its own per status, and its words for screen readers and on hover. */
export const ReadinessIcon = ({
  status,
  label,
  className,
}: {
  status: ReadinessStatus;
  /** what the icon says; without it the icon is decoration next to text */
  label?: string;
  className?: string;
}) => {
  const { Icon, className: color } = READINESS[status];
  if (!label)
    return <Icon className={cn('size-4 shrink-0', color, className)} aria-hidden="true" />;
  return (
    <span role="img" aria-label={label} title={label} className={cn('inline-flex', className)}>
      <Icon className={cn('size-4 shrink-0', color)} aria-hidden="true" />
    </span>
  );
};

/* ─── a check result that cannot be drawn ─── */

/**
 * If drawing a check result fails, the page says why instead of going blank:
 * the stand gave a field in an unexpected shape.
 */
export class ResultBoundary extends Component<
  { children: ReactNode; fallback: (message: string) => ReactNode },
  { error: Error | null }
> {
  override state: { error: Error | null } = { error: null };

  static getDerivedStateFromError(error: Error) {
    return { error };
  }

  override render() {
    return this.state.error ? this.props.fallback(this.state.error.message) : this.props.children;
  }
}

/* ─── confirmations ─── */

export interface ConfirmRequest {
  title: string;
  /** the consequence, spelled out */
  description: string;
  confirmLabel: string;
  destructive?: boolean;
  onConfirm: () => void;
}

/**
 * Every real operation on the stand and every removal asks first. One dialog
 * per panel: `ask` opens it with the question, `dialog` is rendered once.
 */
export const useConfirm = () => {
  const [request, setRequest] = useState<ConfirmRequest | null>(null);
  const dialog = (
    <ConfirmDialog
      open={request !== null}
      onOpenChange={(open) => {
        if (!open) setRequest(null);
      }}
      title={request?.title ?? ''}
      description={request?.description ?? ''}
      confirmLabel={request?.confirmLabel ?? ''}
      destructive={request?.destructive ?? false}
      onConfirm={() => {
        request?.onConfirm();
        setRequest(null);
      }}
    />
  );
  return { ask: setRequest, dialog };
};

/* ─── tabs ─── */

export const TabBar = <T extends string>({
  tabs,
  value,
  onChange,
  label,
}: {
  tabs: { id: T; label: string; icon: LucideIcon }[];
  value: T;
  onChange: (id: T) => void;
  label: string;
}) => (
  <div role="tablist" aria-label={label} className="flex flex-wrap gap-1 border-b border-border">
    {tabs.map(({ id, label: text, icon: Icon }) => {
      const selected = id === value;
      return (
        <button
          key={id}
          type="button"
          role="tab"
          aria-selected={selected}
          onClick={() => onChange(id)}
          className={cn(
            '-mb-px inline-flex items-center gap-1.5 border-b-2 px-3 py-2 text-[13px] font-medium transition-colors',
            selected
              ? 'border-accent text-ink'
              : 'border-transparent text-ink-muted hover:text-ink',
          )}
        >
          <Icon className="size-4" aria-hidden="true" />
          {text}
        </button>
      );
    })}
  </div>
);

/* ─── a panel of the stand actions ─── */

export const PanelHead = ({
  icon: Icon,
  title,
  hint,
  action,
}: {
  icon: LucideIcon;
  title: string;
  hint: ReactNode;
  action?: ReactNode;
}) => (
  <div className="flex items-start justify-between gap-3">
    <div className="min-w-0 flex-1">
      <h3 className="flex items-center gap-2 text-[13px] font-semibold text-ink">
        <Icon className="size-4 shrink-0" aria-hidden="true" />
        {title}
      </h3>
      <p className="mt-0.5 text-[12px] text-ink-muted">{hint}</p>
    </div>
    {action}
  </div>
);
